import * as React from 'react'
import axios from 'axios'
import queryString from 'query-string'
import { useLocale, useTranslation } from 'hooks'

/**
 * A React component that renders a select input for subdivisions based on the selected country.
 * @param {SubdivisionSelectProps} props - The props for the component.
 * @returns {JSX.Element} A select input element with options for subdivisions based on the selected country.
 */
const SubdivisionSelect = ({
  onChange,
  value,
  country,
  error,
  newForm = false,
  modelName,
  disable = false
}) => {
  const [subdivisions, setSubdivisions] = React.useState([])
  const [loading, setLoading] = React.useState(true)
  const [stateError, setStateError] = React.useState(false)

  const { t } = useTranslation()
  const { localiseUrl } = useLocale()

  React.useEffect(() => {
    setLoading(true)
    setStateError(false)
    const query = queryString.stringify({ country })

    axios
      .get(
        localiseUrl(`/api/countries/subdivisions?${query}`, {
          localeAsParam: true
        })
      )
      .then(({ data }) => {
        setSubdivisions(data.subdivisions)
        setLoading(false)
        data.state && onChange && onChange(data.state)
      })
      .catch(({ data }) => {
        setLoading(false)
        onChange && onChange('')
        setStateError(Boolean(t('react_components.address.county_err')))
      })
  }, [country]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   * @param {Object} event
   * @param {HTMLSelectElement} event.target
   */
  const handleChange = ({ target }) => {
    onChange && onChange(target.value)
  }

  let stateName

  switch (country) {
    case 'GB':
    case 'IE':
      stateName = t('react_components.address.county')
      break
    case 'US':
      stateName = t('react_components.address.state')
      break
    default:
      stateName = t('react_components.address.state_province')
  }

  return (
    <div
      className={`${newForm ? 'new-field' : 'field'} field--select${
        error ? ' field--erroneous' : ''
      }`}
    >
      <label className={newForm ? 'new-label' : ''} htmlFor="state">
        {stateName}
      </label>
      <select
        name={`${modelName}[state]`}
        className={newForm ? 'new-input' : ''}
        disabled={disable || subdivisions.length <= 0 || stateError}
        id="state"
        value={loading ? 'loading' : value}
        onChange={handleChange}
      >
        {loading ? (
          <option disabled="disabled" value="loading">
            {t('shared.loading')}
          </option>
        ) : (
          <>
            {/* this is how to specify a placeholder in select */}
            <option value="" disabled selected>
              {t('react_components.address.please_select', {
                label: stateName
              })}
            </option>

            {subdivisions.map((/** @type {Subdivision} */ subdivision, i) => (
              <option key={`subdivision-option-${i}`} value={subdivision.code}>
                {subdivision.display_name}
              </option>
            ))}
          </>
        )}
      </select>
      {error && (
        <span className="field__error-message">
          {stateName} {error}
        </span>
      )}
    </div>
  )
}

/**
 * @typedef {Object} SubdivisionSelectProps
 * @property {Function} onChange
 * @property {string} value
 * @property {string} country
 * @property {string|boolean} error
 * @property {boolean} [newForm]
 * @property {string} modelName
 * @property {boolean} [disable]
 */

/**
 * @typedef {Object} Subdivision
 * @property {string} code
 * @property {string} display_name
 * @property {string} official_name
 * @property {string} gb_name
 * @property {?string} local_name
 * @property {string[]} unofficial_names
 */

export default SubdivisionSelect
